/**
 * StatCard — Reusable metric/KPI card
 */
import { TrendingDown, TrendingUp } from '@mui/icons-material';
import { Box, Card, CardContent, Skeleton, Typography, alpha } from '@mui/material';
import React from 'react';

interface StatCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  color?: string; // icon bg color (hex)
  trend?: number; // percentage, +/-
  trendLabel?: string;
  loading?: boolean;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  color = '#4f46e5',
  trend,
  trendLabel,
  loading = false,
  subtitle,
}) => {
  const trendPositive = trend !== undefined && trend >= 0;

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Skeleton width={100} height={16} sx={{ mb: 1.5 }} />
              <Skeleton width={80} height={36} />
              <Skeleton width={120} height={14} sx={{ mt: 1 }} />
            </Box>
            <Skeleton variant="rounded" width={48} height={48} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        '&:hover': {
          borderColor: alpha(color, 0.4),
          boxShadow: `0 4px 20px ${alpha(color, 0.12)}`,
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                letterSpacing: '0.06em',
                fontWeight: 600,
                fontSize: '0.6875rem',
                display: 'block',
                mb: 1,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                lineHeight: 1.1,
                fontSize: { xs: '1.5rem', md: '1.75rem' },
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                {subtitle}
              </Typography>
            )}
            {trend !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                {trendPositive ? (
                  <TrendingUp sx={{ fontSize: 14, color: '#10b981' }} />
                ) : (
                  <TrendingDown sx={{ fontSize: 14, color: '#ef4444' }} />
                )}
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: trendPositive ? '#10b981' : '#ef4444' }}
                >
                  {trendPositive ? '+' : ''}
                  {trend}%
                </Typography>
                {trendLabel && (
                  <Typography variant="caption" color="text.secondary">
                    {trendLabel}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          {icon && (
            <Box
              aria-hidden="true"
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                flexShrink: 0,
                bgcolor: alpha(color, 0.12),
                color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& svg': { fontSize: '1.375rem' },
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default StatCard;
